import { AssetGroup } from '@play-co/timestep-core';
import { FlexBoxView } from '@play-co/timestep-core/flexbox';
import { ImageScaleView, View } from '@play-co/timestep-core/ui';
import { downscale } from 'src/conf/scale';
import PopupBasicLazy from 'src/game/components/popups/PopupBasicLazy';
import DreamDriftHeroSelectionCard from 'src/game/dreamDrift/components/DreamDriftHeroSelectionCard';
import { dreamDriftCostumeAssets } from 'src/game/dreamDrift/dreamDrift.costumes';
import i18n from 'src/lib/i18n/i18n';
import sounds from 'src/lib/sounds';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import { LangBitmapFontTextShadowView } from 'src/lib/ui/components/LangBitmapFontTextShadowView';
import uiConfig from 'src/lib/ui/config';
import { getScreenDimensions } from 'src/lib/utils';
import {
  dreamDriftHeroes,
  type DreamDriftHero,
} from 'src/replicant/dreamDrift/dreamDrift.ruleset';
import { getRandomItemFromArray } from 'src/replicant/utils/random';

/** Native width of the supplied Dream Drift hero-selection design. */
const DESIGN_WIDTH = 720;

/** Native height of the supplied Dream Drift hero-selection design. */
const DESIGN_HEIGHT = 1280;

/** Lifecycle context controlling the hero-selection confirmation copy. */
export type DreamDriftHeroSelectionMode = 'begin' | 'continue';

/** Arguments accepted when opening the Dream Drift hero-selection popup. */
export type PopupDreamDriftHeroSelectionOpenArgs = {
  mode: DreamDriftHeroSelectionMode;
  selectedHero?: DreamDriftHero;
};

/** Close callback returning the hero confirmed through the popup CTA. */
type PopupDreamDriftHeroSelectionClose = (hero: DreamDriftHero) => void;

/** Fullscreen Dream Drift overlay used to select the next hero power. */
export default class PopupDreamDriftHeroSelection extends PopupBasicLazy<PopupDreamDriftHeroSelectionClose> {
  /** All hero cards keyed by the hero identity they represent. */
  private readonly heroCards = new Map<
    DreamDriftHero,
    DreamDriftHeroSelectionCard
  >();

  /** Current selection returned when the primary action is confirmed. */
  private selectedHero: DreamDriftHero = dreamDriftHeroes[0];

  /** Primary action whose copy reflects the active instance lifecycle. */
  private readonly confirmButton: ButtonScaleViewWithText;

  /**
   * Create the fullscreen overlay and its fixed native-resolution content.
   *
   * @param opts Popup parent and typed close callback supplied by the registry.
   */
  constructor(opts: {
    superview: View;
    close: PopupDreamDriftHeroSelectionClose;
  }) {
    // Size the borderless popup to the complete current viewport.
    const width = opts.superview.style.width;
    const { height } = getScreenDimensions();

    super({
      superview: opts.superview,
      close: opts.close,
      width,
      height,
      zIndex: 10001,
      darkerBg: true,
      skipBackground: true,
      skipTitle: true,
      skipMessage: true,
      tracking: {
        feature: 'dream_drift',
        $subFeature: 'dream_drift_hero_selection',
      },
    });

    // Hero selection must resolve through the confirmation CTA.
    this.buttonClose.removeFromSuperview();

    // Scale the 720-by-1280 Figma composition to shorter or narrower screens.
    const contentScale = Math.min(width / DESIGN_WIDTH, height / DESIGN_HEIGHT);
    const content = new View({
      superview: this.box,
      width: DESIGN_WIDTH,
      height: DESIGN_HEIGHT,
      x: width / 2,
      y: height / 2,
      scale: contentScale,
      centerOnOrigin: true,
    });

    // Place the shared red ribbon at the top of the fullscreen composition.
    const titleRibbon = new ImageScaleView({
      superview: content,
      image: 'assets/ui/dreamDrift/popupHeroSelection/title_ribbon.png',
      width: downscale(776),
      height: downscale(239),
      x: DESIGN_WIDTH / 2,
      y: 116,
      centerOnOrigin: true,
      scaleMethod: 'contain',
    });

    // Render the only Title-font text centered over the ribbon.
    new LangBitmapFontTextShadowView({
      superview: titleRibbon,
      x: titleRibbon.style.width / 2,
      y: titleRibbon.style.height / 2 - 30,
      width: titleRibbon.style.width - 100,
      height: 60,
      centerOnOrigin: true,
      align: 'center',
      verticalAlign: 'center',
      color: '#FFF7D7',
      font: 'Title',
      size: 40,
      textShadowColor: '#512426',
      textShadowOffsetX: 0,
      textShadowOffsetY: 5,
      localeText: () => i18n('dreamDrift.heroSelection.title'),
    });

    // Distribute two exact card rows through one centered flexbox grid.
    const heroCardsContainer = new FlexBoxView({
      superview: content,
      x: 15,
      y: 200,
      width: DESIGN_WIDTH - 30,
      height: 805,
      flex: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignContent: 'space-between',
        justifyContent: 'space-between',
      },
    });

    // Add all configured heroes and let flexbox place them in ruleset order.
    dreamDriftHeroes.forEach((hero) => {
      const card = new DreamDriftHeroSelectionCard({
        superview: heroCardsContainer,
        hero,
        onSelect: (selectedHero) => this.selectHero(selectedHero),
      });
      this.heroCards.set(hero, card);
    });

    // Confirm the active selection with the standard green popup action.
    this.confirmButton = new ButtonScaleViewWithText({
      superview: content,
      ...uiConfig.buttons.secondary,
      x: DESIGN_WIDTH / 2,
      y: 1127,
      width: 470,
      height: 105,
      centerOnOrigin: true,
      font: 'Body',
      fontSize: 48,
      labelOffsetY: -3,
      localeText: () => i18n('dreamDrift.heroSelection.begin'),
      onClick: async () => {
        // Return the selected hero only after the player confirms the choice.
        sounds.sfx.play('ok');
        opts.close(this.selectedHero);
      },
      tracking: {
        feature: 'dream_drift',
        $subFeature: 'dream_drift_hero_selection_begin',
      },
    });
  }

  /**
   * Load the selection chrome, hero emblems, and costumes before display.
   *
   * @returns Asset group containing every popup and hero costume image.
   */
  public getAssetsGroup(): AssetGroup {
    // Load the popup, shared hero icons, and every selectable costume together.
    return new AssetGroup([
      ...AssetGroup.constructURLs([
        'assets/ui/dreamDrift/popupHeroSelection',
        'assets/ui/dreamDrift/common',
      ]),
      ...dreamDriftCostumeAssets,
    ]);
  }

  /**
   * Initialize the selected hero before the popup begins fading in.
   *
   * @param opts Selection mode and optional hero stored on the active instance.
   */
  public init(opts: PopupDreamDriftHeroSelectionOpenArgs): void {
    // Match the primary action copy and tracking to the selection lifecycle.
    this.confirmButton.localeText = () =>
      i18n(`dreamDrift.heroSelection.${opts.mode}`);
    this.confirmButton.tracking.$subFeature = `dream_drift_hero_selection_${opts.mode}`;

    // Preserve the current hero or randomly choose one for a fresh instance.
    const selectedHero =
      opts.selectedHero ??
      getRandomItemFromArray([...dreamDriftHeroes], Math.random);
    this.selectHero(selectedHero);

    // Open the fully configured fullscreen popup.
    super.init({});
  }

  /**
   * Select one hero and synchronize every card's badge.
   *
   * @param hero Hero that should become the popup's active selection.
   */
  private selectHero(hero: DreamDriftHero): void {
    // Store the hero returned if the primary action is confirmed.
    this.selectedHero = hero;

    // Mark only the card associated with the current selection.
    this.heroCards.forEach((card, cardHero) => {
      card.setSelected(cardHero === hero);
    });
  }
}
